import { Loader2, X, AlertTriangle, Info, CheckCircle, AlertCircle } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";

export const DialogModal = ({
    open,
    onClose,
    onSave,
    onDraft,
    title,
    subtitle,
    children,
    showActions = true,
    saveLabel = "Save",
    draftLabel = "Draft",
    editMode = false,
    closeLabel = "Close",
    className,
    blurBackground = 'xs',
    loading = false,
    width = '300px',
    maxWidth = '95vw'
}) => {
    if (!open) return null;

    return (
        <div className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-4",
            `bg-black/40 backdrop-blur-${blurBackground}`, // Fixed template literal issue
            "animate-in fade-in-0 duration-300"
        )}>
            <div
                style={{
                    width: width,
                    // Never exceed viewport on any screen size
                    maxWidth: width
                        ? `min(${maxWidth || width}, calc(100vw - 2rem))`
                        : (maxWidth || 'calc(100vw - 2rem)'),
                }}
                className={cn(
                    "bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 sm:p-4 w-full",
                    "border border-gray-200 dark:border-gray-700",
                    "animate-in zoom-in-95 duration-300",
                    "max-h-[95vh] overflow-y-auto",
                    "mx-auto",
                    className
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex flex-col gap-2">
                        {title && (
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate pr-2">
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <h2 className="text-xs text-gray-400">
                                {subtitle}
                            </h2>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="mb-4 sm:mb-6"> {/* Responsive margin */}
                    {children}
                </div>

                {/* Actions */}
                {showActions && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-end"> {/* Stack on mobile */}
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className={cn(
                                "px-4 py-2 rounded-lg text-sm font-medium",
                                "bg-gray-100 text-gray-700 hover:bg-gray-200",
                                "dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600",
                                "border border-gray-300 dark:border-gray-600",
                                "transition-colors duration-200",
                                "disabled:opacity-50 disabled:cursor-not-allowed",
                                "order-2 sm:order-1" // Reorder for mobile
                            )}
                        >
                            {closeLabel}
                        </button>

                        <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2"> {/* Action buttons group */}
                            {onDraft && !editMode && (
                                <button
                                    onClick={onDraft}
                                    disabled={loading}
                                    className={cn(
                                        "px-4 py-2 rounded-lg text-sm font-medium",
                                        "bg-amber-500 text-white hover:bg-amber-600",
                                        "border border-amber-500",
                                        "focus:ring-2 focus:ring-amber-500 focus:ring-offset-2",
                                        "dark:focus:ring-offset-gray-800",
                                        "transition-colors duration-200",
                                        "disabled:opacity-50 disabled:cursor-not-allowed"
                                    )}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span className="hidden sm:inline">Saving...</span>
                                        </div>
                                    ) : (
                                        draftLabel
                                    )}
                                </button>
                            )}

                            <button
                                onClick={onSave}
                                disabled={loading}
                                className={cn(
                                    "px-4 py-2 rounded-lg text-sm font-medium",
                                    "bg-primary text-white hover:bg-sidebar-primary",
                                    "border border-sidebar-primary",
                                    "focus:ring-2 focus:ring-sidebar-primary focus:ring-offset-2",
                                    "dark:focus:ring-offset-gray-800",
                                    "transition-colors duration-200",
                                    "disabled:opacity-50 disabled:cursor-not-allowed"
                                )}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span className="hidden sm:inline">
                                            {editMode ? "Updating..." : "Saving..."}
                                        </span>
                                    </div>
                                ) : (
                                    saveLabel
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Confirm popup
export const ConfirmPopupModal = ({
    open,
    onClose,
    onConfirm,
    title = "Confirm Action",
    description,
    type = "warning", // warning, info, success, danger
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    className,
    blurBackground = 'xs',
    loading = false,
    destructive = false,
}) => {
    if (!open) return null;

    // Icon and color based on type - using direct classes to avoid dynamic class issues
    const getIconConfig = () => {
        switch (type) {
            case "info":
                return {
                    icon: Info,
                    bgColor: "bg-blue-50 dark:bg-blue-900/20",
                    iconClass: "text-blue-500"
                };
            case "success":
                return {
                    icon: CheckCircle,
                    bgColor: "bg-green-50 dark:bg-green-900/20",
                    iconClass: "text-green-500"
                };
            case "danger":
                return {
                    icon: AlertCircle,
                    bgColor: "bg-red-50 dark:bg-red-900/20",
                    iconClass: "text-red-500"
                };
            case "warning":
            default:
                return {
                    icon: AlertTriangle,
                    bgColor: "bg-amber-50 dark:bg-amber-900/20",
                    iconClass: "text-amber-500"
                };
        }
    };

    const getButtonColor = () => {
        if (destructive) return "bg-red-500 hover:bg-red-600 focus:ring-red-500 border-red-600";
        
        switch (type) {
            case "info": return "bg-primary hover:bg-primary/90 focus:ring-primary border-primary";
            case "success": return "bg-green-600 hover:bg-green-700 focus:ring-green-500 border-green-600";
            case "danger": return "bg-red-600 hover:bg-red-700 focus:ring-red-500 border-red-600";
            case "warning":
            default: return "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 border-amber-600";
        }
    };

    const { icon: Icon, bgColor, iconClass } = getIconConfig();
    const confirmButtonClass = getButtonColor();

    return (
        <div className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4", // Responsive padding
            "bg-black/40 backdrop-blur-xs",
            "animate-in fade-in-0 duration-300"
        )}>
            <div
                className={cn(
                    "bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 sm:p-4 w-full max-w-md mx-4", // Responsive padding and margin
                    "border border-gray-200 dark:border-gray-700",
                    "animate-in zoom-in-95 duration-300",
                    "max-h-[85vh] overflow-y-auto", // Prevent overflow
                    className
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with Icon */}
                <div className="flex items-start gap-3 mb-4">
                    <div className={cn(
                        "flex-shrink-0 p-2 rounded-lg",
                        bgColor,
                        iconClass
                    )}>
                        <Icon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                        {title && (
                            <h2 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-1 break-words">
                                {title}
                            </h2>
                        )}
                        {description && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 break-words">
                                {description}
                            </p>
                        )}
                    </div>

                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50 ml-2"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end"> {/* Stack on mobile, reverse order */}
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className={cn(
                            "px-4 py-2 rounded-lg text-sm font-medium",
                            "bg-gray-100 text-gray-700 hover:bg-gray-200",
                            "dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600",
                            "border border-gray-300 dark:border-gray-600",
                            "transition-colors duration-200",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            "order-2 sm:order-1" // Cancel comes first on mobile
                        )}
                    >
                        {cancelLabel}
                    </button>

                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={cn(
                            "px-4 py-2 rounded-lg text-sm font-medium text-white",
                            "border focus:ring-2 focus:ring-offset-2",
                            "dark:focus:ring-offset-gray-800",
                            "transition-colors duration-200",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            confirmButtonClass,
                            "order-1 sm:order-2" // Confirm comes first on mobile
                        )}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="hidden sm:inline">Processing...</span>
                            </div>
                        ) : (
                            confirmLabel
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};